import { Component, OnDestroy, OnInit } from "@angular/core";
import { Subscription } from "rxjs";
import { QueuedProcess, ProcessQueueState } from "../../../../models/ProcessQueue";
import { ProcessQueueService } from "../../../../services/process-queue.service";
import { TokenStorageService } from "../../../../services/token-storage.service";

@Component({
	selector: "app-process-queue-widget",
	templateUrl: "./process-queue-widget.component.html",
	styleUrls: ["./process-queue-widget.component.scss"],
})
export class ProcessQueueWidgetComponent implements OnInit, OnDestroy {
	emisorId = "";
	processes: QueuedProcess[] = [];
	expanded = false;
	retrying = new Set<string>();

	private companySubscription?: Subscription;
	private requestSubscription?: Subscription;
	private retrySubscriptions = new Subscription();
	private pollTimer?: ReturnType<typeof setTimeout>;
	private completionTimers = new Map<string, ReturnType<typeof setTimeout>>();
	private hiddenCompletedIds = new Set<string>();

	constructor(private queueService: ProcessQueueService, private tokenStorage: TokenStorageService) {}

	ngOnInit(): void {
		const current = this.tokenStorage.getCompany();
		if (current) this.selectCompany(current);

		this.companySubscription = this.tokenStorage.currentEmisorObservable.subscribe((emisorId) => {
			if (emisorId && emisorId !== this.emisorId) this.selectCompany(emisorId);
		});
	}

	ngOnDestroy(): void {
		this.companySubscription?.unsubscribe();
		this.requestSubscription?.unsubscribe();
		this.retrySubscriptions.unsubscribe();
		this.clearPoll();
		this.clearCompletionTimers();
	}

	toggle(): void {
		this.expanded = !this.expanded;
	}

	retry(process: QueuedProcess, event: Event): void {
		event.stopPropagation();
		if (!this.emisorId || this.retrying.has(process.id)) return;

		this.retrying.add(process.id);
		const selectedCompany = this.emisorId;
		this.retrySubscriptions.add(
			this.queueService.retry(selectedCompany, process.id).subscribe(
				() => {
					this.retrying.delete(process.id);
					if (this.emisorId === selectedCompany) this.load();
				},
				() => this.retrying.delete(process.id)
			)
		);
	}

	isProcessing(state: ProcessQueueState): boolean {
		return state === "Procesando";
	}

	isPending(state: ProcessQueueState): boolean {
		return state === "Pendiente" || state === "PendienteReintento";
	}

	isCompleted(state: ProcessQueueState): boolean {
		return state === "Completado" || state === "Omitido";
	}

	isError(state: ProcessQueueState): boolean {
		return state === "Fallido" || state === "CompletadoConError";
	}

	private selectCompany(emisorId: string): void {
		this.requestSubscription?.unsubscribe();
		this.clearPoll();
		this.clearCompletionTimers();
		this.hiddenCompletedIds.clear();
		this.retrying.clear();
		this.processes = [];
		this.expanded = false;
		this.emisorId = emisorId;
		this.load();
	}

	private load(): void {
		if (!this.emisorId) return;
		const selectedCompany = this.emisorId;
		this.requestSubscription?.unsubscribe();
		this.requestSubscription = this.queueService.get(selectedCompany).subscribe(
			(processes) => {
				if (this.emisorId !== selectedCompany) return;
				this.processes = (processes || []).filter(
					(process) => process.emisorId === selectedCompany && !this.hiddenCompletedIds.has(process.id)
				);
				this.scheduleCompletedRemoval();
				if (this.processes.some((process) => this.isActive(process.estado))) this.schedulePoll();
				else this.clearPoll();
			},
			() => this.clearPoll()
		);
	}

	private schedulePoll(): void {
		this.clearPoll();
		this.pollTimer = setTimeout(() => this.load(), 5000);
	}

	private scheduleCompletedRemoval(): void {
		this.processes
			.filter((process) => this.isCompleted(process.estado) && !this.completionTimers.has(process.id))
			.forEach((process) => {
				const timer = setTimeout(() => {
					this.completionTimers.delete(process.id);
					this.hiddenCompletedIds.add(process.id);
					this.processes = this.processes.filter((item) => item.id !== process.id);
				}, 8000);
				this.completionTimers.set(process.id, timer);
			});
	}

	private isActive(state: ProcessQueueState): boolean {
		return state === "Pendiente" || state === "Procesando" || state === "PendienteReintento";
	}

	private clearPoll(): void {
		if (this.pollTimer) clearTimeout(this.pollTimer);
		this.pollTimer = undefined;
	}

	private clearCompletionTimers(): void {
		this.completionTimers.forEach((timer) => clearTimeout(timer));
		this.completionTimers.clear();
	}
}
